import React, { useState } from 'react';
import {
  Server, ShieldCheck, Globe, Lock, Clock, CheckCircle2,
  ArrowRight, PhoneCall, X, User, Phone, Mail, Send, Sparkles, Gauge
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const HostingPage = () => {
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    domain: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const hostingPlans = [
    {
      id: 'shared',
      name: 'NVMe Web Hosting',
      popular: false,
      price: '$4.99',
      period: '/month',
      badge: 'STARTER & SMALL BIZ',
      badgeColor: 'from-blue-500 to-cyan-500',
      description: 'Ultra-fast SSD NVMe cPanel hosting optimized for WordPress & business websites.',
      specs: [
        'Unmetered SSD NVMe Storage',
        'Free SSL & Automated Backups',
        'cPanel & 1-Click App Installer',
        'Unlimited Business Email Accounts',
        '99.9% Uptime Guarantee',
        'DDoS Attack Protection'
      ]
    },
    {
      id: 'vps',
      name: 'High-Performance VPS',
      popular: true,
      price: '$19.99',
      period: '/month',
      badge: 'MOST POPULAR',
      badgeColor: 'from-orange-500 to-amber-500',
      description: 'Dedicated vCPU cores and RAM with root access for scalable web apps and Node.js/Python microservices.',
      specs: [
        'Dedicated vCPU & NVMe RAM',
        'Full Root / SSH Admin Access',
        'Docker & Kubernetes Ready',
        'Isolated Resource Guarantee',
        'Instant Server Scaling',
        '24/7 Managed Server Telemetry'
      ]
    },
    {
      id: 'dedicated',
      name: 'Bare-Metal Dedicated',
      popular: false,
      price: '$89.99',
      period: '/month',
      badge: 'ENTERPRISE BARE-METAL',
      badgeColor: 'from-purple-500 to-indigo-500',
      description: 'Maximum raw hardware performance powered by AMD EPYC / Intel Xeon processors.',
      specs: [
        '100% Dedicated Physical Server',
        'Up to 128GB DDR5 ECC RAM',
        '10 Gbps High-Speed Network Uplink',
        'Hardware RAID Storage Array',
        'Custom OS & Kernel Control',
        'Enterprise SLA Guarantee'
      ]
    },
    {
      id: 'cloud',
      name: 'Enterprise Cloud Infrastructure',
      popular: false,
      price: 'Custom',
      period: 'Quote',
      badge: 'MULTI-CLOUD & DEVOPS',
      badgeColor: 'from-emerald-500 to-teal-500',
      description: 'Multi-region AWS / GCP cloud cluster architecture with automated load balancing and DevOps.',
      specs: [
        'Multi-Cloud High Availability',
        'Automated CI/CD Deployment',
        'Auto-Scaling & Load Balancers',
        'Zero-Downtime Migration',
        'HIPAA & ISO 27001 Security',
        'Dedicated Solutions Architect'
      ]
    }
  ];

  const features = [
    {
      icon: Gauge,
      title: 'Ultra-Fast NVMe SSD Speed',
      desc: 'Experience up to 20x faster read/write speeds with enterprise-grade NVMe SSD arrays and HTTP/3 QUIC protocol.'
    },
    {
      icon: ShieldCheck,
      title: 'DDoS & Malware Defense',
      desc: 'AI-powered web application firewall (WAF), real-time malware scrubbing, and automated daily off-site backups.'
    },
    {
      icon: Globe,
      title: 'Global Server Edge Network',
      desc: 'Deploy servers across 10+ global data centers in US, Europe, Asia, and India for minimal latency.'
    },
    {
      icon: Clock,
      title: '99.99% Uptime SLA',
      desc: 'Redundant power feeds, dual-network uplinks, and automated failovers guarantee non-stop availability.'
    },
    {
      icon: Lock,
      title: 'Free SSL & Security Hardening',
      desc: 'Every hosting account comes with free wildcard SSL certificates, SSH access, and automated security patches.'
    },
    {
      icon: Server,
      title: '24/7 Expert Server Support',
      desc: 'Our certified sysadmins and DevOps engineers are online around the clock to assist with migrations and server tweaks.'
    }
  ];

  const handleOpenModal = (planName) => {
    setSelectedPlan(planName);
    setIsSubmitted(false);
    setIsModalOpen(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmitRequest = (e) => {
    e.preventDefault();

    if (!formData.name || !formData.phone) {
      toast.error('Please enter your name and phone number');
      return;
    }

    const planName = selectedPlan || 'Cloud Hosting';
    const messageText = `Hello HOASP International Hosting Team,%0A%0A*New Hosting Plan Request*%0A- *Plan:* ${planName}%0A- *Client Name:* ${formData.name}%0A- *Mobile:* ${formData.phone}%0A- *Email:* ${formData.email || 'N/A'}%0A- *Domain/Project:* ${formData.domain || 'N/A'}%0A- *Requirements:* ${formData.message || 'Call me back with hosting details & instant setup.'}`;

    // 1. Mobile WhatsApp Trigger
    const whatsappUrl = `https://wa.me/918796118101?text=${messageText}`;

    // 2. Email Mailto Trigger
    const mailtoUrl = `mailto:info@hoasp.com?subject=Hosting Request: ${encodeURIComponent(planName)} - ${encodeURIComponent(formData.name)}&body=${decodeURIComponent(messageText).replace(/%0A/g, '%0D%0A')}`;

    window.open(whatsappUrl, '_blank');

    setTimeout(() => {
      window.location.href = mailtoUrl;
    }, 500);

    setIsSubmitted(true);
    toast.success(`Request submitted for ${planName}! Opening WhatsApp & Email notification.`);
  };

  return (
    <div className="min-h-screen bg-gray-950 pt-20 text-left text-gray-100 font-['Roboto',sans-serif]">
      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-gray-900 via-slate-900 to-gray-950 border-b border-gray-800 relative overflow-hidden">
        {/* Animated Mesh Grid Background */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#1e293b40_1px,transparent_1px),linear-gradient(to_bottom,#1e293b40_1px,transparent_1px)] bg-[size:3rem_3rem] pointer-events-none" />
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-96 h-96 bg-gradient-to-tr from-cyan-500/20 via-blue-500/20 to-transparent rounded-full blur-3xl pointer-events-none" />

        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center space-x-2 bg-cyan-500/10 border border-cyan-500/20 rounded-full px-4 py-1.5 mb-6">
              <Server className="w-4 h-4 text-cyan-400" />
              <span className="text-cyan-300 text-xs md:text-sm font-semibold tracking-wide uppercase">High-Speed Enterprise Cloud & Server Hosting</span>
            </div>

            <h1 className="text-4xl md:text-6xl font-extrabold text-white mb-6 leading-tight tracking-tight">
              Ultra-Fast, Secure & Scalable <br />
              <span className="bg-gradient-to-r from-cyan-400 via-blue-400 to-indigo-500 bg-clip-text text-transparent">Cloud Hosting Infrastructure</span>
            </h1>

            <p className="text-gray-300 text-base md:text-xl mb-8 leading-relaxed max-w-3xl mx-auto">
              Empower your web applications, e-commerce stores, and enterprise databases with NVMe SSD storage, 99.99% uptime SLA, automated security hardening, and 24/7 expert server management.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => handleOpenModal('Custom Server Hosting Setup')}
                className="bg-gradient-to-r from-cyan-500 via-blue-600 to-indigo-600 hover:from-cyan-600 hover:to-indigo-700 text-white px-8 py-4 text-base font-bold rounded-xl shadow-lg shadow-cyan-500/20 flex items-center justify-center gap-2"
              >
                Get Started with Free Migration <ArrowRight className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Hosting Plans Comparison Grid */}
      <section className="py-20 bg-gray-950 border-b border-gray-800">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <span className="text-cyan-400 text-sm font-semibold uppercase tracking-wider">Flexible Hosting Packages</span>
            <h2 className="text-3xl md:text-5xl font-extrabold text-white mt-2 mb-4">Choose Your Ideal Server Environment</h2>
            <p className="text-gray-400 text-base md:text-lg max-w-2xl mx-auto">
              From high-speed shared cPanel hosting to fully isolated bare-metal servers and multi-cloud clusters.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {hostingPlans.map((plan) => (
              <div
                key={plan.id}
                className={`bg-white border rounded-3xl p-6 flex flex-col justify-between transition-all duration-300 hover:scale-[1.03] shadow-xl relative overflow-hidden ${plan.popular ? 'border-orange-500 ring-2 ring-orange-500/20' : 'border-gray-200'
                  }`}
              >
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <span className={`text-[11px] font-black uppercase tracking-wider text-white bg-gradient-to-r ${plan.badgeColor} px-3 py-1 rounded-full shadow-sm`}>
                      {plan.badge}
                    </span>
                  </div>

                  <h3 className="text-xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                  <p className="text-xs text-gray-600 mb-6 leading-relaxed min-h-[40px]">{plan.description}</p>

                  <div className="flex items-baseline mb-6 pb-6 border-b border-gray-200">
                    <span className="text-4xl font-extrabold text-gray-900 tracking-tight">{plan.price}</span>
                    <span className="text-sm font-bold text-gray-500 ml-1.5">{plan.period}</span>
                  </div>

                  <div className="space-y-3 mb-8">
                    {plan.specs.map((spec, idx) => (
                      <div key={idx} className="flex items-center space-x-2.5 text-xs text-gray-700 font-medium">
                        <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                        <span>{spec}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <Button
                  onClick={() => handleOpenModal(plan.name)}
                  className={`w-full py-3.5 rounded-xl font-bold transition-all shadow-md ${plan.popular
                    ? 'bg-gradient-to-r from-orange-500 to-amber-600 hover:from-orange-600 hover:to-amber-700 text-white'
                    : 'bg-gray-900 hover:bg-black text-white'
                    }`}
                >
                  Request Server Setup
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Cloud Features Grid */}
      <section className="py-20 bg-slate-900 border-b border-gray-800">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <span className="text-cyan-400 text-sm font-semibold uppercase tracking-wider">Enterprise Capabilities</span>
            <h2 className="text-3xl md:text-5xl font-extrabold text-white mt-2 mb-4">Engineered for Maximum Speed & Resilience</h2>
            <p className="text-gray-400 text-base md:text-lg max-w-2xl mx-auto">
              Our infrastructure is crafted to meet the strict security, performance, and uptime SLAs required by modern digital enterprises.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((item, index) => (
              <div
                key={index}
                className="bg-gray-950/80 border border-gray-800 rounded-2xl p-6 hover:border-cyan-500/50 transition-all duration-300 group hover:shadow-xl hover:shadow-cyan-500/5"
              >
                <div className="w-14 h-14 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform shadow-md">
                  <item.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3 group-hover:text-cyan-400 transition-colors">{item.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Global Data Center Locations & Security Section */}
      <section className="py-20 bg-slate-50 border-t border-b border-gray-200">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-orange-600 text-sm font-semibold uppercase tracking-wider">Global Infrastructure</span>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-2 mb-6">
                Data Centers Located Near Your Audience
              </h2>
              <p className="text-gray-600 text-base md:text-lg leading-relaxed mb-8">
                Reduce latency and improve Google Core Web Vitals scores by hosting your web applications on edge data center nodes positioned in North America, Europe, Asia Pacific, and India.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex items-center space-x-3 text-gray-800 bg-white border border-gray-200 p-3.5 rounded-xl shadow-sm">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                  <span className="text-sm font-bold">Mumbai & Noida (India)</span>
                </div>
                <div className="flex items-center space-x-3 text-gray-800 bg-white border border-gray-200 p-3.5 rounded-xl shadow-sm">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                  <span className="text-sm font-bold">Virginia & California (USA)</span>
                </div>
                <div className="flex items-center space-x-3 text-gray-800 bg-white border border-gray-200 p-3.5 rounded-xl shadow-sm">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                  <span className="text-sm font-bold">Frankfurt & London (EU)</span>
                </div>
                <div className="flex items-center space-x-3 text-gray-800 bg-white border border-gray-200 p-3.5 rounded-xl shadow-sm">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                  <span className="text-sm font-bold">Singapore & Tokyo (APAC)</span>
                </div>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-3xl p-8 md:p-10 shadow-xl relative overflow-hidden">
              <Server className="w-14 h-14 text-cyan-600 mb-6" />
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Need Custom Server Infrastructure or Migration?</h3>
              <p className="text-gray-600 mb-6 leading-relaxed text-sm md:text-base">
                Our sysadmins offer zero-downtime website and database migration from cPanel, AWS, GoDaddy, Hostinger, or DigitalOcean with zero data loss.
              </p>
              <Button
                onClick={() => handleOpenModal('Custom Server Migration')}
                className="w-full bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 text-white font-bold py-4 rounded-xl shadow-lg"
              >
                Schedule Free Server Migration
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* REQUEST HOSTING MODAL */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-md flex items-center justify-center p-4">
          <div className="bg-white border border-gray-200 rounded-3xl max-w-lg w-full p-6 md:p-8 shadow-2xl relative overflow-hidden animate-in fade-in zoom-in duration-200 text-left">
            {/* Modal Close Button */}
            <button
              onClick={() => setIsModalOpen(false)}
              className="absolute top-5 right-5 text-gray-500 hover:text-gray-900 bg-gray-100 hover:bg-gray-200 p-2 rounded-full transition-colors"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Modal Header */}
            <div className="flex items-center space-x-4 mb-6 pb-4 border-b border-gray-200">
              <div className="w-12 h-12 bg-cyan-100 border border-cyan-200 rounded-xl flex items-center justify-center text-cyan-600 flex-shrink-0">
                <Server className="w-6 h-6" />
              </div>
              <div>
                <span className="text-cyan-600 text-xs font-semibold uppercase tracking-wider flex items-center gap-1">
                  <PhoneCall className="w-3.5 h-3.5" /> Hosting & Server Inquiry
                </span>
                <h3 className="text-xl md:text-2xl font-bold text-gray-900">{selectedPlan}</h3>
              </div>
            </div>

            {isSubmitted ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-emerald-100 border border-emerald-300 rounded-full flex items-center justify-center mx-auto mb-4 text-emerald-600">
                  <CheckCircle2 className="w-10 h-10" />
                </div>
                <h4 className="text-2xl font-bold text-gray-900 mb-2">Request Transmitted!</h4>
                <p className="text-gray-600 text-sm mb-6 leading-relaxed">
                  Your request for <strong className="text-cyan-600">{selectedPlan}</strong> has been transmitted to our hosting support team via WhatsApp and email.
                </p>
                <div className="p-4 bg-slate-50 border border-gray-200 rounded-2xl mb-6 text-xs text-gray-600 text-left space-y-1.5">
                  <div className="flex items-center justify-between">
                    <span>Mobile Notification:</span>
                    <span className="text-emerald-600 font-semibold flex items-center gap-1"><Sparkles className="w-3 h-3" /> Sent to WhatsApp (+91 8796 118 101)</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Email Notification:</span>
                    <span className="text-blue-600 font-semibold">Sent to info@hoasp.com</span>
                  </div>
                </div>
                <Button
                  onClick={() => setIsModalOpen(false)}
                  className="w-full bg-cyan-600 hover:bg-cyan-700 text-white font-bold py-3.5 rounded-xl"
                >
                  Close & Return to Page
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmitRequest} className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Your Full Name *</label>
                  <div className="relative">
                    <User className="w-4 h-4 text-gray-400 absolute left-3.5 top-3.5" />
                    <input
                      type="text"
                      name="name"
                      required
                      placeholder="e.g. Vikram Verma"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full bg-slate-50 border border-gray-300 rounded-xl pl-10 pr-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-cyan-500 focus:bg-white transition-colors"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Mobile Phone Number *</label>
                    <div className="relative">
                      <Phone className="w-4 h-4 text-gray-400 absolute left-3.5 top-3.5" />
                      <input
                        type="tel"
                        name="phone"
                        required
                        placeholder="+91 98765 43210"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="w-full bg-slate-50 border border-gray-300 rounded-xl pl-10 pr-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-cyan-500 focus:bg-white transition-colors"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Email Address</label>
                    <div className="relative">
                      <Mail className="w-4 h-4 text-gray-400 absolute left-3.5 top-3.5" />
                      <input
                        type="email"
                        name="email"
                        placeholder="name@company.com"
                        value={formData.email}
                        onChange={handleInputChange}
                        className="w-full bg-slate-50 border border-gray-300 rounded-xl pl-10 pr-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-cyan-500 focus:bg-white transition-colors"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Domain Name / Website URL</label>
                  <input
                    type="text"
                    name="domain"
                    placeholder="e.g. mycompany.com"
                    value={formData.domain}
                    onChange={handleInputChange}
                    className="w-full bg-slate-50 border border-gray-300 rounded-xl px-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-cyan-500 focus:bg-white transition-colors"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Server Notes / Migration Details</label>
                  <textarea
                    name="message"
                    rows="3"
                    placeholder="Describe RAM, vCPU cores, cPanel accounts, or current host..."
                    value={formData.message}
                    onChange={handleInputChange}
                    className="w-full bg-slate-50 border border-gray-300 rounded-xl px-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-cyan-500 focus:bg-white transition-colors"
                  ></textarea>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-gradient-to-r from-cyan-600 via-blue-600 to-indigo-600 hover:from-cyan-700 hover:to-indigo-700 text-white font-bold py-4 rounded-xl shadow-lg shadow-cyan-500/20 flex items-center justify-center gap-2"
                >
                  <Send className="w-4 h-4" /> Submit Request to Mobile & Email
                </Button>

                <p className="text-[11px] text-gray-500 text-center mt-2">
                  🔒 We respect your privacy. Request sent directly to mobile (+91 8796 118 101) & email.
                </p>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default HostingPage;
